import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Users, 
  Target,
  TrendingUp,
  ChevronRight,
  Loader2,
  LogOut,
  BookOpen,
  UserPlus,
  Bell,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARENT_CHART_TOOLTIP = { borderRadius: '12px', border: '1px solid hsl(214, 32%, 91%)' };

const ParentDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childResults, setChildResults] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      
      const [childrenRes, linksRes, contentRes] = await Promise.all([
        fetch(`${API}/parent/children`, { headers, credentials: 'include' }),
        fetch(`${API}/parent/pending-links`, { headers, credentials: 'include' }),
        fetch(`${API}/parent/content`)
      ]);

      if (childrenRes.ok) {
        const data = await childrenRes.json();
        setChildren(data.children);
      }
      if (linksRes.ok) {
        const data = await linksRes.json();
        setPendingLinks(data.pending_links);
      }
      if (contentRes.ok) {
        const data = await contentRes.json();
        setContent(data.content);
      }
    } catch (error) {
      logger.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleApproveLink = async (linkId) => {
    try {
      const response = await fetch(`${API}/parent/approve-link/${linkId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Child linked successfully');
        fetchData();
      } else {
        toast.error('Failed to approve link');
      }
    } catch (error) {
      toast.error('Failed to approve link');
    }
  };

  const viewChildProgress = async (child) => {
    setSelectedChild(child);
    try {
      const response = await fetch(`${API}/parent/child/${child.user_id}/results`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setChildResults(data.results);
      }
    } catch (error) {
      logger.error('Failed to fetch child results:', error);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map((n) => n[0]).join('').toUpperCase() || 'P';
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Excellent': return 'text-white bg-black';
      case 'Very Good': return 'text-primary bg-primary/10';
      case 'Good': return 'text-amber-600 bg-amber-spark/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-black/85 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="parent-logo">
            <div className="w-10 h-10 rounded-xl bg-[hsl(210,30%,92%)] flex items-center justify-center">
              <Users className="h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
            </div>
            <span className="font-semibold text-lg text-foreground">MindpulseI Parent</span>
          </Link>

          <div className="flex items-center gap-4">
            {pendingLinks.length > 0 && (
              <div className="relative">
                <Bell className="h-5 w-5 text-amber-500" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full text-xs text-white flex items-center justify-center">
                  {pendingLinks.length}
                </span>
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full p-1" data-testid="user-menu-btn">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-[hsl(210,30%,92%)] text-[#2563EB]">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="parent-dashboard">
        {/* Welcome */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Welcome, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground">
              Track your children's emotional intelligence journey
            </p>
          </div>
          <Button 
            className="rounded-full"
            onClick={() => setLinkDialogOpen(true)}
            data-testid="link-child-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Link Child
          </Button>
        </div>

        {/* Pending Link Requests */}
        {pendingLinks.length > 0 && (
          <Card className="rounded-2xl border-amber-200 bg-amber-spark/10  mb-8" data-testid="pending-links">
            <CardContent className="p-6">
              <h3 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Pending Link Requests
              </h3>
              <div className="space-y-3">
                {pendingLinks.map((link) => (
                  <div key={link.link_id} className="flex items-center justify-between p-3 bg-black rounded-xl">
                    <div>
                      <p className="font-medium">{link.student_name}</p>
                      <p className="text-sm text-muted-foreground">{link.student_email}</p>
                    </div>
                    <Button 
                      size="sm" 
                      className="rounded-full"
                      onClick={() => handleApproveLink(link.link_id)}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Children Overview */}
        <Card className="rounded-2xl border-white/10  mb-8" data-testid="children-overview">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Your Children
            </CardTitle>
          </CardHeader>
          <CardContent>
            {children.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-4">
                {children.map((child) => (
                  <Card 
                    key={child.user_id} 
                    className="rounded-2xl border-white/10 card-hover cursor-pointer"
                    onClick={() => viewChildProgress(child)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-white/5 text-white">
                              {getInitials(child.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className="font-semibold">{child.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {child.grade} • Age {child.age}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>

                      {child.latest_result ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Latest Score</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLevelColor(child.latest_result.level)}`}>
                              {child.latest_result.level}
                            </span>
                          </div>
                          <Progress value={child.latest_result.percentage} className="h-2" />
                          <p className="text-right text-sm font-medium">
                            {child.latest_result.percentage.toFixed(0)}%
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-2">
                          No assessments yet
                        </p>
                      )}

                      <p className="text-xs text-muted-foreground mt-3">
                        {child.assessments_completed} assessments completed
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No children linked yet</p>
                <Button 
                  className="rounded-full"
                  onClick={() => setLinkDialogOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Link Your Child
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Parent Resources */}
        <Card className="rounded-2xl border-white/10 " data-testid="parent-resources">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Resources for Parents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4">
              {Object.entries(content).map(([key, topic]) => (
                <Link key={key} to={`/parent/learn/${key}`}>
                  <Card className="rounded-2xl border-white/10 card-hover h-full">
                    <CardContent className="p-5">
                      <h3 className="font-semibold mb-2">{topic.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {topic.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Link Child Dialog */}
      <LinkChildDialog 
        open={linkDialogOpen} 
        onOpenChange={setLinkDialogOpen}
        onSuccess={fetchData}
      />

      {/* Child Progress Dialog */}
      {selectedChild && (
        <ChildProgressDialog
          child={selectedChild}
          results={childResults}
          open={!!selectedChild}
          onOpenChange={(open) => !open && setSelectedChild(null)}
        />
      )}
    </div>
  );
};

const LinkChildDialog = ({ open, onOpenChange, onSuccess }) => {
  const { getAuthHeaders } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLink = async () => {
    if (!email) {
      toast.error('Please enter your child\'s email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/parent/link-child`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ child_email: email })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Linked to ${data.student_name}`);
        setEmail('');
        onOpenChange(false);
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to link child');
      }
    } catch (error) {
      toast.error('Failed to link child');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="link-child-dialog">
        <DialogHeader>
          <DialogTitle>Link Your Child</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <p className="text-sm text-muted-foreground">
            Enter your child's email address to link their account and track their EI progress.
          </p>
          <Input
            type="email"
            placeholder="child@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-xl"
            data-testid="child-email-input"
          />
          <Button 
            onClick={handleLink}
            disabled={loading}
            className="w-full rounded-full"
            data-testid="link-child-confirm-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
            Link Child
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ChildProgressDialog = ({ child, results, open, onOpenChange }) => {
  const getLevelColor = (level) => {
    switch (level) {
      case 'Excellent': return 'text-white bg-black';
      case 'Very Good': return 'text-primary bg-primary/10';
      case 'Good': return 'text-amber-600 bg-amber-spark/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const chartData = results.slice().reverse().map((r, i) => ({
    name: `Test ${i + 1}`,
    score: r.percentage
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="child-progress-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-white/5 text-white">
                {child.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <span>{child.name}'s Progress</span>
              <p className="text-sm font-normal text-muted-foreground">
                {child.grade} • Age {child.age}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Latest Score */}
          {child.latest_result && (
            <div className="p-4 rounded-2xl bg-muted/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Current EI Score</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(child.latest_result.level)}`}>
                  {child.latest_result.level}
                </span>
              </div>
              <div className="text-3xl font-bold">{child.latest_result.percentage.toFixed(0)}%</div>
            </div>
          )}

          {/* Progress Chart */}
          {chartData.length > 1 && (
            <div>
              <h4 className="font-semibold mb-3">Progress Over Time</h4>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                    <XAxis dataKey="name" stroke="hsl(215, 16%, 47%)" fontSize={10} />
                    <YAxis domain={[0, 100]} stroke="hsl(215, 16%, 47%)" fontSize={10} />
                    <Tooltip contentStyle={PARENT_CHART_TOOLTIP} />
                    <Line type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Category Breakdown */}
          {child.latest_result?.category_scores && (
            <div>
              <h4 className="font-semibold mb-3">Skills Breakdown</h4>
              <div className="space-y-3">
                {Object.entries(child.latest_result.category_scores).map(([cat, score]) => (
                  <div key={cat}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{cat.replace('_', ' ')}</span>
                      <span className="text-muted-foreground">{score}%</span>
                    </div>
                    <Progress value={score} className="h-2" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {child.latest_result?.recommendations?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3">Recommendations</h4>
              <ul className="space-y-2">
                {child.latest_result.recommendations.map((rec, i) => (
                  <li key={rec} className="flex items-start gap-2 text-sm p-3 rounded-xl bg-muted/50">
                    <span className="text-primary font-medium">{i + 1}.</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {results.length === 0 && (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {child.name} hasn't taken any assessments yet.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ParentDashboard;
